import type { Message } from '@ai-sdk/react';

// Tier-1 persistence: keep the *current* thread in localStorage so a reload or
// PWA relaunch restores it. Deliberately local-only — no server data-at-rest,
// and durable facts already live in the memory service. "New chat" clears it.
const KEY = 'neuro.chat.v1';
const MAX = 100; // cap stored turns so storage can't grow unbounded

export function loadChat(): Message[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const arr: unknown = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    // Keep only well-formed entries; tolerate schema drift from older versions.
    const ok = arr.filter(
      (m): m is Message =>
        !!m &&
        typeof m === 'object' &&
        typeof (m as Message).role === 'string' &&
        typeof (m as Message).content === 'string',
    );
    return ok.slice(-MAX);
  } catch {
    return [];
  }
}

export function saveChat(messages: Message[]): void {
  try {
    if (messages.length === 0) {
      localStorage.removeItem(KEY);
      return;
    }
    // Image attachments are ephemeral by design (base64 data URLs, never
    // uploaded) — drop them before persisting so a couple of photos can't
    // blow the localStorage quota and silently kill history for everything.
    const withoutAttachments = messages
      .slice(-MAX)
      .map(({ experimental_attachments: _drop, ...rest }) => rest);
    localStorage.setItem(KEY, JSON.stringify(withoutAttachments));
  } catch {
    // private mode / quota exceeded — persistence is best-effort, never fatal
  }
}

export function clearChat(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* no-op */
  }
}
